import { TabBar } from "../../components/TabBar";
import { Button } from "../../components/Button";
import { InlineNameEditor } from "../../components/InlineNameEditor";
import { QuestionText } from "../../components/QuestionText";

export interface TutorStarterQuestion {
  questionId: string;
  stem: string;
  source: string;
}

export interface TutorStarterSubject {
  subjectId: string;
  subject: string;
  questions: TutorStarterQuestion[];
}

export interface TutorStarterTopic {
  topicId: string;
  name: string;
  subject: string;
}

interface TutorStartersScreenProps {
  firstName: string;
  /** Wrong answers, already grouped by subject. */
  subjects: TutorStarterSubject[];
  topics: TutorStarterTopic[];
  onPickQuestion: (questionId: string) => void;
  onPickTopic: (topicId: string) => void;
  onPractise: () => void;
  /** Inline name save — lets the student add their name without leaving. */
  onSaveName?: ((firstName: string) => Promise<void>) | undefined;
}

/**
 * Screen 17, opened cold from the tab bar.
 *
 * The tutor is pinned to a question on purpose — an explanation with nothing
 * attached to it is unverifiable. So the tab used to fail outright when opened
 * with no question, showing "We couldn't load that." and taking the tab bar
 * down with it.
 *
 * Nothing here is invented. The questions are ones this student actually got
 * wrong; the topics are their real weaknesses. A student with neither is told
 * so plainly and sent to practise, because there is genuinely nothing to
 * explain yet.
 */
export function TutorStartersScreen({
  firstName,
  subjects,
  topics,
  onPickQuestion,
  onPickTopic,
  onPractise,
  onSaveName,
}: TutorStartersScreenProps) {
  const empty = subjects.length === 0 && topics.length === 0;

  return (
    <div className="app-column">
      <div className="flex-1 px-5 pb-6 pt-6">
        <h1 className="m-0 font-[family-name:var(--font-display)] text-[20px] font-bold">
          What should we work on?
        </h1>
        <p className="m-0 pt-1.5 text-[13px] leading-[1.5] text-text-muted">
          {empty
            ? `Answer a few questions first, ${firstName} — then I can explain the ones you miss.`
            : "Pick something and I'll explain it."}
        </p>

        {onSaveName ? <InlineNameEditor firstName={firstName} onSave={onSaveName} /> : null}

        {subjects.length > 0 ? (
          <>
            <h2 className="m-0 pt-6 text-[11px] font-semibold uppercase tracking-[0.1em] text-text-muted">
              You got these wrong
            </h2>
            {/*
              One section per subject. A student arrives here thinking about a
              subject, not about chronology, and a mixed list makes them read
              every row to find the two that are theirs.
            */}
            {subjects.map((group) => (
              <section key={group.subjectId} className="pt-4">
                <h3 className="m-0 text-[12.5px] font-semibold">{group.subject}</h3>
                <ul className="m-0 flex list-none flex-col gap-2 p-0 pt-2">
                  {group.questions.map((question) => (
                    <li key={question.questionId}>
                      <button
                        type="button"
                        onClick={() => onPickQuestion(question.questionId)}
                        className="flex w-full flex-col gap-1 rounded-[var(--radius-control)] border border-border bg-surface px-3.5 py-3 text-left"
                      >
                        {question.source ? (
                          <span className="text-[11.5px] text-text-muted">{question.source}</span>
                        ) : null}
                        <QuestionText className="line-clamp-2 text-[13.5px] leading-[1.45]">
                          {question.stem}
                        </QuestionText>
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </>
        ) : null}

        {topics.length > 0 ? (
          <>
            <h2 className="m-0 pt-6 text-[11px] font-semibold uppercase tracking-[0.1em] text-text-muted">
              Your weakest topics
            </h2>
            <ul className="m-0 flex list-none flex-col gap-2 p-0 pt-3">
              {topics.map((topic) => (
                <li key={topic.topicId}>
                  <button
                    type="button"
                    onClick={() => onPickTopic(topic.topicId)}
                    className="flex w-full items-center justify-between gap-3 rounded-[var(--radius-control)] border border-border bg-surface px-3.5 py-3 text-left"
                  >
                    <span className="flex flex-col gap-0.5">
                      <span className="text-[13.5px]">{topic.name}</span>
                      <span className="text-[11.5px] text-text-muted">{topic.subject}</span>
                    </span>
                    {/* Practice, not tutoring: the tutor needs a question, and
                        a topic is not one. Saying so beats pretending. */}
                    <span className="shrink-0 text-[11.5px] text-accent">Practise →</span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        ) : null}

        {empty ? (
          <div className="pt-6">
            <Button onClick={onPractise}>Start practising</Button>
          </div>
        ) : null}
      </div>

      <TabBar />
    </div>
  );
}
